import React, { useEffect, useRef, useState, useCallback, useMemo, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { reportsAPI } from '../services/api';
import { SIMULATE_CHANGED_EVENT } from '../services/simulate';
import api from '../services/api';
import InfoIconButton from '../components/InfoIconButton';
import { useReportsHubNavigation } from '../contexts/ReportsHubNavigationContext';
import './SchoolReferralNeededReport.css';

function csvEscape(val) {
  if (val === null || val === undefined) return '';
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function formatDate(val) {
  if (val === null || val === undefined) return '';
  const s = String(val).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${Number(m[2])}/${Number(m[3])}/${m[1]}`;
  return s;
}

/** Table shows district number (e.g. #131); filter uses full school name. */
function formatDistrictDisplay(schoolDistrict) {
  if (schoolDistrict == null || String(schoolDistrict).trim() === '') return '—';
  const s = String(schoolDistrict).trim();
  return /^\d+$/.test(s) ? `#${s}` : s;
}

const COLUMNS = [
  { key: 'client_name', label: 'Client', sortable: true },
  { key: 'birthdate', label: 'Birthdate', sortable: true },
  { key: 'age_months', label: 'Age (mo)', sortable: true, numeric: true },
  { key: 'staff_name', label: 'Staff', sortable: true, filter: true },
  { key: 'school', label: 'District', sortable: true, filter: true },
  { key: 'language', label: 'Language', sortable: true },
  { key: 'ifsp_date', label: 'IFSP Date', sortable: true },
  { key: 'consent_on_file', label: 'Consent', sortable: true, filter: true },
];

function compare(a, b, key) {
  const va = a[key];
  const vb = b[key];
  if (va == null && vb == null) return 0;
  if (va == null) return 1;
  if (vb == null) return -1;
  if (key === 'birthdate' || key === 'ifsp_date') {
    const da = new Date(va);
    const db = new Date(vb);
    return da.getTime() - db.getTime();
  }
  if (typeof va === 'number' && typeof vb === 'number') return va - vb;
  return String(va).localeCompare(String(vb), undefined, { sensitivity: 'base' });
}

/**
 * School Referral Needed report (legacy SC Report option 33).
 * Open clients aged 27–30 months with no school referral date. Consent column shows whether
 * a signed school consent document is on file. Sort via column headers; filter on Staff,
 * District, and Consent columns.
 */
const REPORT_DESCRIPTION = 'Not closed; no school referral date; between 27 and 30 months old. The Consent column shows whether a signed school consent document is on file in the client\'s Intake-Consents folder. Sort by clicking column headers; use the filter icon on Staff, District, or Consent to narrow results.';

export default function SchoolReferralNeededReport({ embedded = false, refreshKey = 0, tenantCode = null }) {
  const reportsHubNav = useReportsHubNavigation();
  const [searchParams] = useSearchParams();
  const filterMe = searchParams.get('filter') === 'me';
  const filterMeApplied = useRef(false);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [roleCode, setRoleCode] = useState(null);
  const [myStaffId, setMyStaffId] = useState(null);
  const [staffDisplayName, setStaffDisplayName] = useState(null);
  const [tenantCodeFromWhoami, setTenantCodeFromWhoami] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);

  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortKey, setSortKey] = useState('birthdate');
  const [sortDir, setSortDir] = useState('asc');
  const [staffFilter, setStaffFilter] = useState(new Set());
  const [schoolFilter, setSchoolFilter] = useState(new Set());
  const [consentFilter, setConsentFilter] = useState(new Set());
  const [openFilterColumn, setOpenFilterColumn] = useState(null);
  const [draftStaffFilter, setDraftStaffFilter] = useState(new Set());
  const [draftSchoolFilter, setDraftSchoolFilter] = useState(new Set());
  const [draftConsentFilter, setDraftConsentFilter] = useState(new Set());
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0, width: 220 });

  const fetchWhoami = useCallback(async () => {
    try {
      const res = await api.get('/whoami');
      const code = res.data?.membership?.role_code || null;
      const sid = res.data?.user?.staff_id ?? null;
      setRoleCode(code);
      setMyStaffId(sid != null ? String(sid) : null);
      setStaffDisplayName(res.data?.staff_display_name ?? null);
      setTenantCodeFromWhoami(res.data?.tenant?.code ?? null);
    } catch {
      setRoleCode(null);
      setMyStaffId(null);
      setStaffDisplayName(null);
      setTenantCodeFromWhoami(null);
    }
  }, []);

  useEffect(() => {
    fetchWhoami();
  }, [fetchWhoami]);

  useEffect(() => {
    const handler = () => fetchWhoami();
    window.addEventListener(SIMULATE_CHANGED_EVENT, handler);
    return () => window.removeEventListener(SIMULATE_CHANGED_EVENT, handler);
  }, [fetchWhoami]);

  useEffect(() => {
    if (!filterMe || filterMeApplied.current || !staffDisplayName) return;
    filterMeApplied.current = true;
    setStaffFilter(new Set([staffDisplayName]));
  }, [filterMe, staffDisplayName]);

  const runReport = useCallback(() => {
    setError(null);
    setLoading(true);
    reportsAPI
      .get('school-referral-needed', {})
      .then((res) => {
        setRows(res.data?.rows || []);
        setError(null);
      })
      .catch((err) => {
        setRows([]);
        setError(err.response?.data?.error || 'Failed to load report.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    runReport();
  }, [runReport, refreshKey]);

  const handleSortClick = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const displayedRows = useMemo(() => {
    let out = rows;
    if (staffFilter.size > 0) {
      out = out.filter((r) => staffFilter.has(String(r.staff_name ?? '').trim() || '(blank)'));
    }
    if (schoolFilter.size > 0) {
      out = out.filter((r) => schoolFilter.has(String(r.school ?? '').trim() || '(blank)'));
    }
    if (consentFilter.size > 0 && consentFilter.size < 2) {
      out = out.filter((r) => consentFilter.has(r.consent_on_file ? 'Yes' : 'No'));
    }
    out = [...out].sort((a, b) => {
      const cmp = compare(a, b, sortKey);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return out;
  }, [rows, staffFilter, schoolFilter, consentFilter, sortKey, sortDir]);

  const distinctStaff = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => set.add(String(r.staff_name ?? '').trim() || '(blank)'));
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [rows]);

  const distinctSchool = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => set.add(String(r.school ?? '').trim() || '(blank)'));
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [rows]);

  const distinctConsent = ['No', 'Yes'];

  useEffect(() => {
    if (!filterAnchor || !openFilterColumn) return;
    const r = filterAnchor.getBoundingClientRect();
    const width = openFilterColumn === 'school' ? 380 : 220;
    const padding = 12;
    let left = r.left;
    if (left + width > window.innerWidth - padding) {
      left = window.innerWidth - width - padding;
    }
    if (left < padding) {
      left = padding;
    }
    let top = r.bottom;
    const maxHeight = 320;
    if (top + maxHeight > window.innerHeight - padding) {
      top = Math.max(padding, r.top - maxHeight);
    }
    setPopoverPosition({ top, left, width });
  }, [filterAnchor, openFilterColumn]);

  const openFilter = (colKey, ev) => {
    ev.stopPropagation();
    setFilterAnchor(ev.currentTarget);
    if (colKey === 'staff_name') {
      setDraftStaffFilter(new Set(staffFilter));
    } else if (colKey === 'school') {
      setDraftSchoolFilter(new Set(schoolFilter));
    } else if (colKey === 'consent_on_file') {
      setDraftConsentFilter(new Set(consentFilter));
    }
    setOpenFilterColumn(colKey);
  };

  const applyFilter = () => {
    if (openFilterColumn === 'staff_name') {
      setStaffFilter(new Set(draftStaffFilter));
    } else if (openFilterColumn === 'school') {
      setSchoolFilter(new Set(draftSchoolFilter));
    } else if (openFilterColumn === 'consent_on_file') {
      setConsentFilter(new Set(draftConsentFilter));
    }
    setOpenFilterColumn(null);
    setFilterAnchor(null);
  };

  const clearFilter = () => {
    if (openFilterColumn === 'staff_name') {
      setStaffFilter(new Set());
      setDraftStaffFilter(new Set());
    } else if (openFilterColumn === 'school') {
      setSchoolFilter(new Set());
      setDraftSchoolFilter(new Set());
    } else if (openFilterColumn === 'consent_on_file') {
      setConsentFilter(new Set());
      setDraftConsentFilter(new Set());
    }
    setOpenFilterColumn(null);
    setFilterAnchor(null);
  };

  const toggleDraftStaff = (val) => {
    setDraftStaffFilter((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      return next;
    });
  };

  const toggleDraftSchool = (val) => {
    setDraftSchoolFilter((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      return next;
    });
  };

  const toggleExpand = (clientId) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const toggleDraftConsent = (val) => {
    setDraftConsentFilter((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      return next;
    });
  };

  const downloadCsv = () => {
    const headers = ['client_name', 'birthdate', 'age_months', 'staff_name', 'district', 'language', 'ifsp_date', 'consent_on_file'];
    const lines = [
      headers.join(','),
      ...displayedRows.map((r) =>
        [
          csvEscape(r.client_name),
          csvEscape(formatDate(r.birthdate)),
          csvEscape(r.age_months),
          csvEscape(r.staff_name),
          csvEscape(formatDistrictDisplay(r.school_district)),
          csvEscape(r.language),
          csvEscape(formatDate(r.ifsp_date)),
          csvEscape(r.consent_on_file ? 'Yes' : 'No'),
        ].join(',')
      ),
    ];
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'school-referral-needed.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handlePrint = () => {
    window.print();
  };

  const isSc = roleCode === 'SERVICE_COORDINATOR';
  const titleSuffix = tenantCode || tenantCodeFromWhoami ? ` — ${tenantCode || tenantCodeFromWhoami}` : '';
  const scDisplayName = staffDisplayName || (myStaffId ? `ID ${myStaffId}` : null);
  const hasResults = rows.length > 0;

  const activeFilterSummary = useMemo(() => {
    const parts = [];
    if (staffFilter.size > 0) {
      const names = [...staffFilter].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
      parts.push(`Staff: ${names.join(', ')}`);
    }
    if (schoolFilter.size > 0) {
      const schools = [...schoolFilter].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
      parts.push(`District: ${schools.join(', ')}`);
    }
    if (consentFilter.size > 0 && consentFilter.size < 2) {
      parts.push(`Consent: ${[...consentFilter].join(', ')}`);
    }
    return parts;
  }, [staffFilter, schoolFilter, consentFilter]);

  const filterPopoverAriaLabel =
    openFilterColumn === 'staff_name' ? 'Filter Staff' :
    openFilterColumn === 'consent_on_file' ? 'Filter Consent' :
    'Filter School';

  const activeFilterCount = (col) => {
    if (col.key === 'staff_name') return staffFilter.size;
    if (col.key === 'consent_on_file') return consentFilter.size;
    return schoolFilter.size;
  };

  const filterPopover =
    openFilterColumn &&
    createPortal(
      <div
        className={`school-referral-needed-filter-popover ${openFilterColumn === 'school' ? 'school-referral-needed-filter-popover-wide' : ''}`}
        style={{
          position: 'fixed',
          top: `${popoverPosition.top}px`,
          left: `${popoverPosition.left}px`,
          width: `${popoverPosition.width}px`,
        }}
        role="dialog"
        aria-label={filterPopoverAriaLabel}
      >
        <div className="school-referral-needed-filter-popover-actions">
          <button type="button" className="btn btn-secondary" onClick={clearFilter}>Clear</button>
          <button type="button" className="btn btn-primary" onClick={applyFilter}>Apply</button>
        </div>
        <div className="school-referral-needed-filter-popover-list">
          {openFilterColumn === 'staff_name' &&
            distinctStaff.map((val) => (
              <label key={val} className="school-referral-needed-filter-check">
                <input
                  type="checkbox"
                  checked={draftStaffFilter.has(val)}
                  onChange={() => toggleDraftStaff(val)}
                />
                <span>{val}</span>
              </label>
            ))}
          {openFilterColumn === 'school' &&
            distinctSchool.map((val) => (
              <label key={val} className="school-referral-needed-filter-check">
                <input
                  type="checkbox"
                  checked={draftSchoolFilter.has(val)}
                  onChange={() => toggleDraftSchool(val)}
                />
                <span>{val}</span>
              </label>
            ))}
          {openFilterColumn === 'consent_on_file' &&
            distinctConsent.map((val) => (
              <label key={val} className="school-referral-needed-filter-check">
                <input
                  type="checkbox"
                  checked={draftConsentFilter.has(val)}
                  onChange={() => toggleDraftConsent(val)}
                />
                <span>{val}</span>
              </label>
            ))}
        </div>
        <div className="school-referral-needed-filter-popover-actions">
          <button type="button" className="btn btn-secondary" onClick={clearFilter}>Clear</button>
          <button type="button" className="btn btn-primary" onClick={applyFilter}>Apply</button>
        </div>
      </div>,
      document.body
    );

  return (
    <div className="school-referral-needed-report">
      <div className="school-referral-needed-title-row">
        <h2 className="school-referral-needed-title">School Referral Needed{titleSuffix}</h2>
        <InfoIconButton
          onClick={() => setInfoOpen((v) => !v)}
          ariaExpanded={infoOpen}
          ariaControls="school-referral-needed-info"
          ariaLabel="Show report description"
          ariaLabelExpanded="Hide report description"
          className="school-referral-needed-info-icon"
        />
      </div>
      {infoOpen && (
        <p id="school-referral-needed-info" className="school-referral-needed-info-text">
          {REPORT_DESCRIPTION}
        </p>
      )}
      {isSc && scDisplayName && (
        <p className="school-referral-needed-context" aria-live="polite">
          Staff / Service Coordinator: {scDisplayName}
        </p>
      )}
      <div className="school-referral-needed-actions">
        <button
          type="button"
          className="btn btn-outline"
          onClick={() => {
            if (reportsHubNav?.closeActiveReport) {
              reportsHubNav.closeActiveReport('School / ES');
              return;
            }
            setRows([]);
            setError(null);
            setStaffFilter(new Set());
            setSchoolFilter(new Set());
            setConsentFilter(new Set());
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary school-referral-needed-download-btn"
          onClick={downloadCsv}
          disabled={displayedRows.length === 0}
        >
          Export CSV
        </button>
        <button
          type="button"
          className="btn btn-primary school-referral-needed-print-btn"
          onClick={handlePrint}
          disabled={displayedRows.length === 0}
        >
          Print
        </button>
      </div>

      {activeFilterSummary.length > 0 && (
        <p className="school-referral-needed-filter-summary no-print">
          Filters: {activeFilterSummary.join(' | ')}
        </p>
      )}

      {loading && rows.length === 0 && (
        <p className="school-referral-needed-loading" aria-live="polite">Loading…</p>
      )}
      {error && (
        <div className="school-referral-needed-error" role="alert">
          {error}
        </div>
      )}

      {hasResults && (
        <div className="school-referral-needed-results">
          <p className="school-referral-needed-count">
            {displayedRows.length} client{displayedRows.length !== 1 ? 's' : ''}
            {(staffFilter.size > 0 || schoolFilter.size > 0 || (consentFilter.size > 0 && consentFilter.size < 2)) && ' (filtered)'}.
          </p>
          <div className="school-referral-needed-table-wrap">
            <table className="school-referral-needed-table">
              <thead>
                <tr>
                  <th className="school-referral-needed-expand-th" aria-label="Expand" />
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => col.sortable && handleSortClick(col.key)}
                      className={`${col.sortable ? 'school-referral-needed-sortable' : ''} ${col.numeric ? 'school-referral-needed-cell-num' : ''}`.trim()}
                    >
                      <div className="school-referral-needed-header-cell">
                        <span className="school-referral-needed-header-label">
                          {col.label}
                          {col.sortable && sortKey === col.key && (
                            <span className="school-referral-needed-sort-arrow" aria-hidden="true">
                              {sortDir === 'asc' ? ' ↑' : ' ↓'}
                            </span>
                          )}
                        </span>
                        {col.filter && (
                          <span className="school-referral-needed-header-filter-wrap" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className={`school-referral-needed-filter-trigger ${activeFilterCount(col) > 0 ? 'is-active' : ''}`}
                              onClick={(e) => openFilter(col.key, e)}
                              aria-label={`Filter ${col.label}`}
                            >
                              <svg className="school-referral-needed-filter-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M4 6H20L14 13V18L10 20V13L4 6Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedRows.map((row, idx) => {
                  const tc = tenantCode || tenantCodeFromWhoami;
                  const isExpanded = expandedRows.has(row.client_id);
                  return (
                    <Fragment key={idx}>
                      <tr>
                        <td className="school-referral-needed-expand-cell">
                          {row.consent_doc_id && (
                            <button
                              type="button"
                              className="school-referral-needed-expand-btn"
                              onClick={() => toggleExpand(row.client_id)}
                              aria-label={isExpanded ? 'Collapse' : 'Expand'}
                              aria-expanded={isExpanded}
                            >
                              {isExpanded ? '▾' : '▸'}
                            </button>
                          )}
                        </td>
                        {COLUMNS.map((col) => {
                          let cellVal = row[col.key];
                          if (col.key === 'client_name') {
                            return (
                              <td key={col.key}>
                                {tc && row.client_id
                                  ? <a href={`/${tc}/client/${row.client_id}`}>{cellVal || '—'}</a>
                                  : (cellVal || '—')}
                              </td>
                            );
                          }
                          if (col.key === 'birthdate' || col.key === 'ifsp_date') cellVal = formatDate(cellVal) || '—';
                          else if (col.key === 'school') cellVal = formatDistrictDisplay(row.school_district);
                          else if (col.key === 'consent_on_file') cellVal = cellVal ? 'Yes' : 'No';
                          else if (cellVal == null) cellVal = '—';
                          return (
                            <td key={col.key} className={col.numeric ? 'school-referral-needed-cell-num' : ''}>
                              {cellVal}
                            </td>
                          );
                        })}
                      </tr>
                      {isExpanded && row.consent_doc_id && (
                        <tr className="school-referral-needed-expanded-row">
                          <td />
                          <td colSpan={COLUMNS.length}>
                            <a href={`/${tc}/documents/view/${row.consent_doc_id}`} target="_blank" rel="noopener noreferrer">
                              View consent
                            </a>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {filterPopover}
    </div>
  );
}
